import React, { Component } from 'react';
import { Drawer, Button, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import colors from 'utils/colors';
import { DialogWrapper } from 'components/DialogWrapper';
import QuestionnaireAnswers from 'containers/Manage-Questionnaire/questionnaire/QuestionnaireAnswers';
import { QuestionDetails } from 'components/forms/questionnaire-management/question-details';
import {
  DrawerHeader,
  FlexRow,
  BlueTextPointer,
  PlainButton,
  BorderWrapper,
} from 'components/styled-components';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import FormTextAreaGroup from 'components/forms/components/form-text-area-group';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import {
  updateQuestionnaireQuestion,
  updateQuestionnaireDraftQuestion,
  archiveQuestionnaireQuestion,
} from 'api/questionnaire';
import { AppToaster } from 'components/AppToaster';
import {
  Remove,
  HeaderControls,
  PageNumbers,
  ScrollWrapper,
} from 'containers/Manage-Questionnaire/styled-components';
import { statusByVersionStatus } from 'utils/functions';

const mapQuestionTypes = (questionTypes, questionTypeID) => {
  if (typeof questionTypeID === 'number') {
    return filter(questionTypes, ['typeID', questionTypeID]);
  } else {
    return questionTypeID;
  }
};

const mapDisplayCategories = (displayCategories, displayCategory) => {
  if (typeof displayCategory === 'number') {
    return filter(displayCategories, ['categoryID', displayCategory]);
  } else {
    return displayCategory;
  }
};

class QuestionnaireEditQuestionDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editMade: false,
      question: cloneDeep(this.props.question),
      removeQuestion: false,
      closeWithoutSaving: false,
      questionError: false,
      controlError: false,
      weightError: false,
    };
  }

  componentDidUpdate(prevProps, _) {
    if (!isEqual(prevProps.question, this.props.question)) {
      this.setState({ question: cloneDeep(this.props.question) });
    }
  }

  handleCloseDrawer = () => {
    if (this.state.editMade) {
      this.toggleCloseWithoutSaving();
    } else {
      this.resetComponent();
      this.props.closeDrawer();
      this.props.setQuestionnaireIndex(null);
    }
  };

  resetComponent = () =>
    this.setState({
      question: cloneDeep(this.props.question),
      editMade: false,
      questionError: false,
    });

  handlePageChange = nextPage => {
    this.props.closeDrawer();
    this.props.setQuestionnaireIndex(nextPage);
  };

  toggleEditMade = question => {
    if (isEqual(question, this.props.question)) {
      this.setState({ editMade: false });
    } else {
      this.setState({ editMade: true });
    }
  };

  handleTextEdit = ({ target }) => {
    const { value } = target;
    const { question } = this.state;
    question.question = value;
    this.setState({ question });
    this.toggleEditMade(question);
    this.validateQuestionValue(value);
  };

  validateQuestionValue = questionValue => {
    if (questionValue.length > 0) {
      this.setState({ questionError: false });
      this.toggleEditMade(this.state.question);
    } else {
      this.setState({ questionError: true });
    }
  };

  handleQuestionTypeChange = questionType => {
    const { question } = this.state;
    question.questionTypeID = questionType.typeID;
    this.setState({ question });
    this.toggleEditMade(question);
  };

  handleDisplayCategoryChange = displayCategory => {
    const { question } = this.state;
    question.displayCategoryID = displayCategory.categoryID;
    this.setState({ question });
    this.toggleEditMade(question);
  };

  handleControlIDChange = e => {
    const controlID = e.target.value;
    const { question } = this.state;
    question.controlID = controlID;
    this.setState({ question });
    this.validateControlID(controlID);
  };

  handleWeightChange = weight => {
    const { question } = this.state;
    question.weight = isNaN(parseInt(weight)) ? '' : parseInt(weight);
    this.setState({ question });
    this.validateWeightValue(weight);
  };

  handleDefaultChange = defaultQuestion => {
    const { question } = this.state;
    question.defaultQuestion = defaultQuestion;
    this.setState({ question });
    this.toggleEditMade(question);
  };

  validateControlID = controlID => {
    if (controlID !== '') {
      this.setState({ controlError: false });
      this.toggleEditMade(this.state.question);
    } else {
      this.setState({ controlError: true });
    }
  };

  validateWeightValue = weight => {
    if (weight !== '') {
      this.setState({ weightError: false });
      this.toggleEditMade(this.state.question);
    } else {
      this.setState({ weightError: true });
    }
  };

  handleDismissRemoveQuestion = () => this.setState({ removeQuestion: false });
  toggleCloseWithoutSaving = () =>
    this.setState({ closeWithoutSaving: !this.state.closeWithoutSaving });

  handleArchiveQuestion = () => {
    const { questionID, versionStatus } = this.state.question;
    archiveQuestionnaireQuestion(questionID, versionStatus)
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.resetComponent();
        this.props.fetchQuestionnaireQuestions();
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  handleUpdateQuestion = () => {
    const { question } = this.state;
    if (question.versionStatus === 'Active') {
      updateQuestionnaireQuestion(question)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.fetchQuestionnaireQuestions();
          this.setState({ editMade: false }, this.handleCloseDrawer);
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    } else {
      updateQuestionnaireDraftQuestion(question)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.fetchQuestionnaireQuestions();
          this.setState({ editMade: false }, this.handleCloseDrawer);
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    }
  };

  render() {
    const { question, questionError, weightError, controlError } = this.state;
    const { index, total, currentIndex, isOpen, mapping } = this.props;
    const status = statusByVersionStatus(question.versionStatus);

    return (
      <Drawer
        isOpen={isOpen || isEqual(currentIndex, index)}
        onClose={this.handleCloseDrawer}
      >
        <DrawerHeader>
          Edit Question
          {this.state.editMade ? (
            <FlexRow fontSize="0.7rem" width="15rem">
              <BlueTextPointer onClick={this.toggleCloseWithoutSaving}>
                DISCARD
              </BlueTextPointer>
              <PlainButton
                onClick={this.handleUpdateQuestion}
                disabled={questionError || weightError || controlError}
              >
                SAVE CHANGES
              </PlainButton>
            </FlexRow>
          ) : (
            <HeaderControls>
              <PageNumbers>
                {mapping ? index.index + 1 : index + 1} of {total}
              </PageNumbers>
              <Button
                icon={IconNames.CHEVRON_LEFT}
                onClick={
                  mapping
                    ? () =>
                        this.handlePageChange({
                          ...index,
                          index: index.index - 1,
                        })
                    : () => this.handlePageChange(currentIndex - 1)
                }
                disabled={mapping ? index.index === 0 : index === 0}
                minimal
              />
              <Button
                icon={IconNames.CHEVRON_RIGHT}
                onClick={
                  mapping
                    ? () =>
                        this.handlePageChange({
                          ...index,
                          index: index.index + 1,
                        })
                    : () => this.handlePageChange(currentIndex + 1)
                }
                disabled={
                  mapping ? index.index === total - 1 : index === total - 1
                }
                minimal
              />
              <Button
                icon={IconNames.CROSS}
                onClick={this.handleCloseDrawer}
                minimal
              />
            </HeaderControls>
          )}
        </DrawerHeader>
        <ScrollWrapper>
          {this.state.questionError && (
            <ErrorWrapper>The question field cannot be blank.</ErrorWrapper>
          )}
          <FormTextAreaGroup
            label="Text"
            requiredLabel={false}
            name="question"
            fill={true}
            value={question.question}
            handleChange={this.handleTextEdit}
            style={{ background: colors.formBackground }}
          />
          <BorderWrapper>
            <QuestionnaireAnswers
              answers={question.answers}
              openParent={this.props.openParent}
              isParentOpen={this.props.isParentOpen}
              question={question}
              fetchQuestionnaireQuestions={
                this.props.fetchQuestionnaireQuestions
              }
              categories={this.props.categories}
            />
          </BorderWrapper>
          <QuestionDetails
            questionTypes={this.props.questionTypes}
            categories={this.props.categories}
            weight={question.weight}
            controlID={question.controlID}
            defaultQuestion={question.defaultQuestion}
            handleControlIDChange={this.handleControlIDChange}
            handleWeightChange={this.handleWeightChange}
            handleQuestionTypeChange={this.handleQuestionTypeChange}
            handleCategoryChange={this.handleDisplayCategoryChange}
            handleDefaultChange={this.handleDefaultChange}
            category={mapDisplayCategories(
              this.props.categories,
              question.displayCategoryID
            )}
            questionType={mapQuestionTypes(
              this.props.questionTypes,
              question.questionTypeID
            )}
            controlError={controlError}
            weightError={weightError}
          />

          <Remove onClick={() => this.setState({ removeQuestion: true })}>
            {status.getTitle}
          </Remove>

          <DialogWrapper
            isOpen={this.state.removeQuestion}
            onClose={this.handleDismissRemoveQuestion}
            title={status.getTitle}
            body={status.getBody}
            confirm={status.getConfirm}
            action={this.handleArchiveQuestion}
            actionArgs={this.state.question.questionID}
          />
          <DialogWrapper
            isOpen={this.state.closeWithoutSaving}
            onClose={this.toggleCloseWithoutSaving}
            title="You have unsaved changes on this page"
            body="If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page?"
            confirm="LEAVE PAGE"
            action={() => {
              this.setState(
                { editMade: false, closeWithoutSaving: false },
                this.handleCloseDrawer
              );
            }}
          />
        </ScrollWrapper>
      </Drawer>
    );
  }
}

export default QuestionnaireEditQuestionDrawer;
